<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf, helpers  } from '@vuelidate/validators'
import FileUpload from '../../Shared/FileUpload.vue'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import SSNInput from '../../Shared/SSNInput.vue'


interface ChildPension {
  name: string
  ssn: string
  housing: string | null
}

const props = defineProps<{ disable?: boolean }>()
const { umsokn, onLoaded } = useUmsokn()




const rules = computed(() => {
  // 👇 Create explicit reactive dependency on hasChildren
  const hasChildren = umsokn.value?.hasChildren === true

  return {
    umsokn: {
      hasChildren: {},
      childPension: Array.isArray(umsokn.value?.childPension)
        ? umsokn.value.childPension.map((child: ChildPension) => ({
            name: {
              required: requiredIf(() => hasChildren)
            },
            ssn: {
              required: requiredIf(() => hasChildren),
            },
            housing: {
              required: requiredIf(() => hasChildren)
            },
            fileUpload: {
              required: requiredIf(() => hasChildren && child.housing === 'no')
            }
          }))
        : []
    }
  }
})

const v$ = useVuelidate(rules, { umsokn })

onLoaded(async () => {
  // Ensure umsokn.value exists and has a childPension array
  if (!Array.isArray(umsokn.value.childPension) || umsokn.value.childPension.length === 0) {
    umsokn.value.childPension = [{ name: '', ssn: '', housing: null }] as ChildPension[]
  }

  // Wait for next render cycle to rebuild rules
  await nextTick()
  v$.value.$reset()
})


function addChild() {
  umsokn.value?.childPension.push({ name: '', ssn: '', housing: null })
}

function removeChild(index: number) {
  umsokn.value?.childPension.splice(index, 1)
  if (umsokn.value.childPension.length === 0) {
    umsokn.value.childPension.push({ name: '', ssn: '', housing: null })
  }
}

defineExpose({
  async validate() {
    await nextTick() // wait for rules to rebuild if hasChildren changed
    if (Array.isArray(umsokn.value.childPension)) {
      umsokn.value.childPension.forEach((_: ChildPension, i: number) => {
        v$.value.umsokn.childPension[i].$touch()
      })
    }
    const isValid = await v$.value.$validate()
    return !v$.value.$invalid && isValid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">{{ $t('ororku_umsokn_step3_header_1') }}</h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <p>{{ $t('ororku_umsokn_step3_header_claimer') }}</p>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <p class="form__header" style="font-size:18px">{{ $t('ororku_umsokn_step3_header_2') }}</p>
      </v-col>
    </v-row>

    <!-- Has children -->
    <v-row>
      <v-col cols="12">
        <p>{{ $t('ororku_umsokn_step3_haskid') }}</p>
        <v-radio-group
          v-model="umsokn.hasChildren"
          :readonly="props.disable"
          hide-details
          inline
        >
          <v-radio :label="$t('ororku_umsokn_step3_haskid_yes')" :value="true" />
          <v-radio :label="$t('ororku_umsokn_step3_haskid_no')" :value="false" />
        </v-radio-group>
      </v-col>
    </v-row>

    <transition-group name="list">
      <div
        v-if="umsokn.hasChildren"
        v-for="(child, index) in umsokn.childPension"
        :key="'childPension_' + index"
      >
        <div v-if="index > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              type="button"
              class="forms__warning__list__wrapper"
              @click="removeChild(index)"
            >
              <img class="forms__warning__icon" src="@/assets/icons/Close.svg" alt="Remove" />
            </button>
          </div>


        <v-row>
          <!-- Name -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="child.name"
              variant="outlined"
              density="comfortable"
              :readonly="props.disable"
              :label="$t('ororku_umsokn_step3_nafn')"
              :error="v$.umsokn.childPension[index]?.name?.$error"
              :error-messages="v$.umsokn.childPension[index]?.name?.$dirty
                ? [
                    v$.umsokn.childPension[index]?.name?.required?.$invalid
                      ? $t('ororku_umsokn_step3_nafn_error')
                      : ''
                  ].filter(Boolean)
                : []"
            />
          </v-col>

          <!-- SSN -->
          <v-col cols="12" md="6">
            <SSNInput
              v-model="child.ssn"
              :disabled="props.disable"
              :label="$t('ororku_umsokn_step3_ssn')"
              :error="v$.umsokn.childPension[index]?.ssn?.$error"
              :error-messages="v$.umsokn.childPension[index]?.ssn?.$dirty
              ? [
                  v$.umsokn.childPension[index]?.ssn?.required?.$invalid
                    ? $t('ororku_umsokn_step3_ssn_error')
                    : '',
                  v$.umsokn.childPension[index]?.ssn?.optionalKennitala?.$invalid
                    ? $t('barnalifeyrir_umsokn_step3_ssn_invalid')
                    : ''
                ].filter(Boolean)
              : []"
            />
          </v-col>
        </v-row>

        <!-- Housing -->
        <v-row>
          <v-col cols="12">
            <p>{{ $t('ororku_umsokn_step3_childlocation') }}</p>
            <v-radio-group
              v-model="child.housing"
              :readonly="props.disable"
              hide-details
              :error="v$.umsokn.childPension[index]?.housing?.$error"
              :error-messages="v$.umsokn.childPension[index]?.housing?.$dirty
                ? [
                    v$.umsokn.childPension[index]?.housing?.required?.$invalid
                      ? $t('ororku_umsokn_step3_childlocation_error')
                      : ''
                  ].filter(Boolean)
                : []"
              inline
            >
              <v-radio :label="$t('ororku_umsokn_step3_yes')" value="yes" />
              <v-radio :label="$t('ororku_umsokn_step3_no')" value="no" />
            </v-radio-group>
          </v-col>
        </v-row>

        <!-- File upload -->
        <v-row v-if="child.housing === 'no'">
          <v-col cols="12">
            <p class="form__header" style="font-size:18px">
              {{ $t('ororku_umsokn_step3_file_header') }}
            </p>
            <FileUpload
              :umsoknType="3"
              :umsoknId="umsokn.id"
              :filespace="index"
              :disable="props.disable"
            />
            <p>{{ $t('ororku_umsokn_step3_file_disclaimer') }}</p>
          </v-col>
        </v-row>
      </div>
    </transition-group>

    <!-- Add child -->
    <div class="m-t-40" v-if="!props.disable && umsokn.hasChildren">
      <v-btn
        color="primary"
        variant="outlined"
        prepend-icon="mdi-plus"
        class="mt-4"
        @click="addChild"
      >
        {{ $t('ororku_umsokn_step3_add') }}
      </v-btn>
    </div>
  </div>
</template>
